import React, { useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { useGoals } from '../contexts/GoalContext';
import { useAccounts } from '../contexts/AccountContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Plus, Target, MoreVertical, Edit2, Trash2, Check, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';
import Confetti from 'react-confetti';

const GOAL_COLORS = [
  { name: 'Coral', value: '#E8544F' },
  { name: 'Blue', value: '#5B9FE3' },
  { name: 'Green', value: '#4CAF50' },
  { name: 'Purple', value: '#9C27B0' },
  { name: 'Orange', value: '#FF9800' },
  { name: 'Teal', value: '#00BCD4' },
];

const Goals = () => {
  const { goals, loading, addGoal, updateGoal, deleteGoal, markGoalComplete, updateGoalProgress } = useGoals();
  const { accounts } = useAccounts();
  const { theme, isDark } = useTheme();
  const { formatCurrency, displayCurrency, getCurrencyInfo } = useCurrency();
  const currencyInfo = getCurrencyInfo(displayCurrency);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [deletingGoal, setDeletingGoal] = useState(null);
  const [showAddAmountDialog, setShowAddAmountDialog] = useState(false);
  const [amountGoal, setAmountGoal] = useState(null);
  const [amountToAdd, setAmountToAdd] = useState('');
  const [goalForm, setGoalForm] = useState({
    name: '',
    targetAmount: '',
    currentAmount: '',
    targetDate: '',
    linkedAccount: '',
    color: '#E8544F'
  });

  const activeGoals = useMemo(() => goals.filter(g => !g.isCompleted), [goals]);
  const completedGoals = useMemo(() => goals.filter(g => g.isCompleted), [goals]);

  const handleOpenAddDialog = () => {
    setEditingGoal(null);
    setGoalForm({
      name: '',
      targetAmount: '',
      currentAmount: '0',
      targetDate: '',
      linkedAccount: '',
      color: '#E8544F'
    });
    setShowGoalDialog(true);
  };

  const handleOpenEditDialog = (goal) => {
    setEditingGoal(goal);
    setGoalForm({
      name: goal.name,
      targetAmount: goal.targetAmount.toString(),
      currentAmount: goal.currentAmount.toString(),
      targetDate: goal.targetDate ? format(goal.targetDate, 'yyyy-MM-dd') : '',
      linkedAccount: goal.linkedAccount || '',
      color: goal.color || '#E8544F'
    });
    setShowGoalDialog(true);
  };

  const handleSaveGoal = async () => {
    if (!goalForm.name.trim()) {
      toast.error('Please enter a goal name');
      return;
    }
    if (!goalForm.targetAmount || parseFloat(goalForm.targetAmount) <= 0) {
      toast.error('Please enter a valid target amount');
      return;
    }

    try {
      const goalData = {
        name: goalForm.name.trim(),
        targetAmount: parseFloat(goalForm.targetAmount),
        currentAmount: parseFloat(goalForm.currentAmount) || 0,
        targetDate: goalForm.targetDate ? new Date(goalForm.targetDate) : null,
        linkedAccount: goalForm.linkedAccount === 'none' ? null : goalForm.linkedAccount,
        color: goalForm.color
      };

      if (editingGoal) {
        await updateGoal(editingGoal.id, goalData);
        toast.success('Goal updated!');
      } else {
        await addGoal(goalData);
        toast.success('Goal created!');
      }

      setShowGoalDialog(false);
      setEditingGoal(null);
    } catch (error) {
      toast.error('Failed to save goal');
    }
  };

  const handleDeleteGoal = async () => {
    if (!deletingGoal) return;
    try {
      await deleteGoal(deletingGoal.id);
      toast.success('Goal deleted');
      setShowDeleteDialog(false);
      setDeletingGoal(null);
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const handleMarkComplete = async (goal) => {
    try {
      await markGoalComplete(goal.id);
      setShowConfetti(true);
      toast.success('🎉 Congratulations! Goal completed!');
      setTimeout(() => setShowConfetti(false), 5000);
    } catch (error) {
      toast.error('Failed to mark goal complete');
    }
  };

  const calculateProjectedCompletion = (goal) => {
    if (goal.currentAmount >= goal.targetAmount) return 'Complete!';
    if (goal.currentAmount === 0) return 'Start saving to see projection';
    
    const daysElapsed = differenceInDays(new Date(), goal.createdAt);
    if (daysElapsed === 0) return 'Just started';
    
    const dailyRate = goal.currentAmount / daysElapsed;
    const remaining = goal.targetAmount - goal.currentAmount;
    const daysToComplete = Math.ceil(remaining / dailyRate);
    
    const projectedDate = new Date();
    projectedDate.setDate(projectedDate.getDate() + daysToComplete);
    
    return format(projectedDate, 'MMM d, yyyy');
  };

  return (
    <Layout>
      {showConfetti && <Confetti recycle={false} numberOfPieces={500} />}
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold" style={{ color: theme.text }}>Goals</h1>
          <Button
            onClick={handleOpenAddDialog}
            className="flex items-center gap-2"
            style={{ backgroundColor: theme.accent, color: 'white' }}
            data-testid="add-goal-btn"
          >
            <Plus className="w-4 h-4" />
            Add Goal
          </Button>
        </div>

        {/* Goals List */}
        {loading ? (
          <div className="text-center py-12">
            <p style={{ color: theme.textSecondary }}>Loading goals...</p>
          </div>
        ) : activeGoals.length === 0 && completedGoals.length === 0 ? (
          <div 
            className="text-center py-16 rounded-xl"
            style={{ backgroundColor: theme.cardBackground, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
          >
            <div 
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
              style={{ backgroundColor: isDark ? '#2C2C2E' : '#F8F9FA' }}
            >
              <Target className="w-8 h-8" style={{ color: theme.accent }} />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: theme.text }}>
              No goals yet
            </h3>
            <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>
              Tap + to add your first savings goal
            </p>
            <Button
              onClick={handleOpenAddDialog}
              style={{ backgroundColor: theme.accent, color: 'white' }}
            >
              Add Goal
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Active Goals */}
            {activeGoals.length > 0 && (
              <>
                <h2 className="text-sm font-medium uppercase px-1" style={{ color: theme.textSecondary }}>
                  Active Goals ({activeGoals.length})
                </h2>
                {activeGoals.map(goal => {
                  const progress = Math.min((goal.currentAmount / goal.targetAmount) * 100, 100);
                  const isComplete = progress >= 100;

                  return (
                    <div
                      key={goal.id}
                      className="rounded-xl p-4 transition-all hover:shadow-lg"
                      style={{ 
                        backgroundColor: theme.cardBackground, 
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        borderLeft: `4px solid ${goal.color || theme.accent}`
                      }}
                      data-testid={`goal-card-${goal.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `${goal.color}20` }}
                          >
                            <Target className="w-5 h-5" style={{ color: goal.color }} />
                          </div>
                          <div>
                            <h3 className="font-semibold" style={{ color: theme.text }}>{goal.name}</h3>
                            <p className="text-sm" style={{ color: theme.textSecondary }}>
                              Target: {formatCurrency(goal.targetAmount)}
                            </p>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-2 rounded-lg hover:bg-opacity-10 hover:bg-gray-500">
                              <MoreVertical className="w-5 h-5" style={{ color: theme.textSecondary }} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent style={{ backgroundColor: theme.cardBackground }}>
                            <DropdownMenuItem
                              onClick={() => {
                                setAmountGoal(goal);
                                setAmountToAdd('');
                                setShowAddAmountDialog(true);
                              }}
                              style={{ color: theme.text }}
                            >
                              <Plus className="w-4 h-4 mr-2" /> Add Amount
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenEditDialog(goal)} style={{ color: theme.text }}>
                              <Edit2 className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            {isComplete && (
                              <DropdownMenuItem onClick={() => handleMarkComplete(goal)} style={{ color: '#4CAF50' }}>
                                <Check className="w-4 h-4 mr-2" /> Mark Complete
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => { setDeletingGoal(goal); setShowDeleteDialog(true); }}
                              style={{ color: theme.accent }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span style={{ color: theme.text }}>{formatCurrency(goal.currentAmount)}</span>
                          <span style={{ color: goal.color }}>{progress.toFixed(0)}% complete</span>
                        </div>
                        <div 
                          className="h-3 rounded-full overflow-hidden"
                          style={{ backgroundColor: isDark ? '#3A3A3C' : '#E9ECEF' }}
                        >
                          <div 
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${progress}%`, backgroundColor: goal.color || '#4CAF50' }}
                          />
                        </div>
                      </div>

                      {/* Projected Completion */}
                      <div className="flex items-center justify-between text-sm">
                        <span style={{ color: theme.textSecondary }}>
                          Projected: {calculateProjectedCompletion(goal)}
                        </span>
                        {goal.linkedAccount && (
                          <span style={{ color: theme.textSecondary }}>
                            Linked: {goal.linkedAccount}
                          </span>
                        )}
                      </div>

                      {/* Mark Complete Button for 100% */}
                      {isComplete && (
                        <Button
                          onClick={() => handleMarkComplete(goal)}
                          className="w-full mt-3 flex items-center justify-center gap-2"
                          style={{ backgroundColor: '#4CAF50', color: 'white' }}
                        >
                          <Sparkles className="w-4 h-4" />
                          Mark as Complete
                        </Button>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {/* Completed Goals */}
            {completedGoals.length > 0 && (
              <>
                <h2 className="text-sm font-medium uppercase px-1 mt-6" style={{ color: theme.textSecondary }}>
                  Completed ({completedGoals.length})
                </h2>
                {completedGoals.map(goal => (
                  <div
                    key={goal.id}
                    className="rounded-xl p-4 opacity-75"
                    style={{ 
                      backgroundColor: theme.cardBackground, 
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      borderLeft: `4px solid #4CAF50`
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-100">
                          <Check className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold" style={{ color: theme.text }}>{goal.name}</h3>
                          <p className="text-sm" style={{ color: theme.textSecondary }}>
                            {formatCurrency(goal.targetAmount)} achieved! 🎉
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => { setDeletingGoal(goal); setShowDeleteDialog(true); }}
                        className="p-2 rounded-lg hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" style={{ color: theme.accent }} />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Add/Edit Goal Dialog */}
        <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
          <DialogContent style={{ backgroundColor: theme.cardBackground }}>
            <DialogHeader>
              <DialogTitle style={{ color: theme.text }}>
                {editingGoal ? 'Edit Goal' : 'Add Goal'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label style={{ color: theme.textSecondary }}>Goal Name</Label>
                <Input
                  value={goalForm.name}
                  onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })}
                  placeholder="e.g., Trip to Goa"
                  className="mt-1"
                  style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}
                />
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Target Amount ({currencyInfo.symbol})</Label>
                <Input
                  type="number"
                  value={goalForm.targetAmount}
                  onChange={(e) => setGoalForm({ ...goalForm, targetAmount: e.target.value })}
                  placeholder="50000"
                  className="mt-1"
                  style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}
                />
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Current Saved ({currencyInfo.symbol})</Label>
                <Input
                  type="number"
                  value={goalForm.currentAmount}
                  onChange={(e) => setGoalForm({ ...goalForm, currentAmount: e.target.value })}
                  placeholder="0"
                  className="mt-1"
                  style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}
                />
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Target Date (Optional)</Label>
                <Input
                  type="date"
                  value={goalForm.targetDate}
                  onChange={(e) => setGoalForm({ ...goalForm, targetDate: e.target.value })}
                  className="mt-1"
                  style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}
                />
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Linked Account (Optional)</Label>
                <Select 
                  value={goalForm.linkedAccount} 
                  onValueChange={(value) => setGoalForm({ ...goalForm, linkedAccount: value })}
                >
                  <SelectTrigger className="mt-1" style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {accounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.name}>{acc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Color</Label>
                <div className="flex gap-2 mt-2">
                  {GOAL_COLORS.map(c => (
                    <button
                      key={c.value}
                      onClick={() => setGoalForm({ ...goalForm, color: c.value })}
                      className="w-8 h-8 rounded-full border-2 transition-all"
                      style={{ 
                        backgroundColor: c.value,
                        borderColor: goalForm.color === c.value ? theme.text : 'transparent',
                        transform: goalForm.color === c.value ? 'scale(1.1)' : 'scale(1)'
                      }}
                      title={c.name}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGoalDialog(false)} style={{ color: theme.text }}>
                Cancel
              </Button>
              <Button onClick={handleSaveGoal} style={{ backgroundColor: theme.accent, color: 'white' }}>
                {editingGoal ? 'Update' : 'Create'} Goal
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent style={{ backgroundColor: theme.cardBackground }}>
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color: theme.text }}>Delete Goal</AlertDialogTitle>
              <AlertDialogDescription style={{ color: theme.textSecondary }}>
                Are you sure you want to delete &ldquo;{deletingGoal?.name}&rdquo;? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteGoal} style={{ backgroundColor: '#DC3545' }}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Add Amount Dialog */}
        <Dialog open={showAddAmountDialog} onOpenChange={setShowAddAmountDialog}>
          <DialogContent style={{ backgroundColor: theme.cardBackground }}>
            <DialogHeader>
              <DialogTitle style={{ color: theme.text }}>
                {amountGoal ? `Add Amount to "${amountGoal.name}"` : 'Add Amount'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label style={{ color: theme.textSecondary }}>
                  Amount to Add ({currencyInfo.symbol})
                </Label>
                <Input
                  type="number"
                  value={amountToAdd}
                  onChange={(e) => setAmountToAdd(e.target.value)}
                  placeholder="e.g., 1000"
                  className="mt-1"
                  style={{
                    backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF',
                    color: theme.text,
                    borderColor: theme.divider,
                  }}
                />
              </div>
              {amountGoal && (
                <p className="text-xs" style={{ color: theme.textSecondary }}>
                  Current saved: {formatCurrency(amountGoal.currentAmount)} of{' '}
                  {formatCurrency(amountGoal.targetAmount)}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddAmountDialog(false);
                  setAmountGoal(null);
                  setAmountToAdd('');
                }}
                style={{ borderColor: theme.divider, color: theme.text }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!amountGoal) {
                    setShowAddAmountDialog(false);
                    return;
                  }
                  const value = parseFloat(amountToAdd);
                  if (!value || value <= 0) {
                    toast.error('Please enter a valid amount');
                    return;
                  }
                  try {
                    const newTotal = (amountGoal.currentAmount || 0) + value;
                    await updateGoalProgress(amountGoal.id, newTotal);
                    toast.success('Amount added to goal!');
                    setShowAddAmountDialog(false);
                    setAmountGoal(null);
                    setAmountToAdd('');
                  } catch (err) {
                    toast.error('Failed to update goal progress');
                  }
                }}
                style={{ backgroundColor: theme.accent, color: 'white' }}
              >
                Add Amount
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Goals;
